import React, { useState, useEffect } from "react";
import { GameState, GAME_CONSTANTS, INITIAL_STATE } from "./lib/gameLogic";
import GameDashboard from "./components/GameDashboard";
import EventSystem from "./components/EventSystem.tsx";
import SocialFeed from "./components/SocialFeed.js";
import "./App.css";
import AngelInvestor from "./components/AngelInvestor.tsx";
import LaunchSequence from "./components/LaunchSequence";

const App = () => {
  const [gameInstance, setGameInstance] = useState(null);
  const [gameState, setGameState] = useState({
    ...INITIAL_STATE,
    companyName: "",
    showMetrics: false,
    isLaunched: false,
  });
  const [isPaused, setIsPaused] = useState(false);
  const [showLaunchSequence, setShowLaunchSequence] = useState(true);
  const [showAngelInvestor, setShowAngelInvestor] = useState(false);

  const updateGameState = React.useCallback(
    (newState) => {
      const updatedState = {
        ...gameState,
        ...newState,
        isLaunched:
          newState.isLaunched !== undefined
            ? newState.isLaunched
            : gameState.isLaunched,
        showMetrics:
          newState.showMetrics !== undefined
            ? newState.showMetrics
            : gameState.showMetrics,
        adFrequency: newState.adFrequency ?? gameState.adFrequency,
        engagement: newState.engagement ?? gameState.engagement,
        reputation: newState.reputation ?? gameState.reputation,
      };

      if (gameInstance) {
        gameInstance.setState(updatedState);
      }
      setGameState(updatedState);
    },
    [gameInstance, gameState]
  );

  // Initialize game instance only once
  useEffect(() => {
    const game = new GameState();
    setGameInstance(game);

    const unsubscribe = game.subscribe((newState) => {
      setGameState((prevState) => ({
        ...newState,
        companyName: prevState.companyName,
        showMetrics: prevState.showMetrics,
        isLaunched: prevState.isLaunched,
      }));
    });

    return () => {
      unsubscribe();
    };
  }, []);

  // Game loop effect - now checks for both isLaunched and showMetrics
  useEffect(() => {
    if (!gameInstance || !gameState.isLaunched || !gameState.showMetrics)
      return;

    console.log("Starting game loop - metrics shown");
    const gameLoop = setInterval(() => {
      if (!isPaused) {
        gameInstance.update();
      }
    }, GAME_CONSTANTS.TICK_RATE_MS);

    return () => {
      clearInterval(gameLoop);
    };
  }, [gameInstance, isPaused, gameState.isLaunched, gameState.showMetrics]);

  const gameActions = {
    changeAdFrequency: (delta) => gameInstance?.changeAdFrequency(delta),
    changeAdPersonalization: (delta) =>
      gameInstance?.changeAdPersonalization(delta),
    changeAlgorithmLevel: (delta) => {
      gameInstance?.changeAlgorithmLevel(delta);
      gameInstance?.changeAdPersonalization(0);
    },
    upgradeAlgorithm: () => gameInstance?.upgradeAlgorithm(),
  };

  const handleNameSet = (name) => {
    setGameState((prev) => ({ ...prev, companyName: name }));
  };

  const handleLaunchComplete = () => {
    setShowLaunchSequence(false);
    setGameState((prev) => ({ ...prev, isLaunched: true }));
    console.log("Game Launched! isLaunched:", true);
  };

  const handleInitialPostsComplete = () => {
    setTimeout(() => {
      setShowAngelInvestor(true);
    }, 2000);
  };

  useEffect(() => {
    if (!showLaunchSequence && gameState.isLaunched) {
      handleInitialPostsComplete();
    }
  }, [showLaunchSequence, gameState.isLaunched]);

  return (
    <div className={`app ${isPaused ? "paused" : ""}`}>
      {showLaunchSequence && (
        <LaunchSequence
          gameState={gameState}
          onNameSet={handleNameSet}
          onComplete={handleLaunchComplete}
          setGameState={updateGameState}
        />
      )}
      {showAngelInvestor && (
        <AngelInvestor gameState={gameState} onStateUpdate={updateGameState} />
      )}
      <div className="header">
        <h1>{"Enshittification"}</h1>
      </div>

      <div className="BaseContainer">
        {gameState.showMetrics && (
          <GameDashboard gameState={gameState} gameActions={gameActions} />
        )}
        <SocialFeed gameState={gameState} />
      </div>
      <EventSystem
        gameState={gameState}
        setGameState={updateGameState}
        setIsPaused={setIsPaused}
      />
    </div>
  );
};

export default App;
